/**
 * TransactionItem
 *
 * 개별 거래 아이템
 * - 카테고리 아이콘
 * - 메모
 * - 금액
 * - 카테고리, 결제수단, 지출자 (2줄 구조, 반응형)
 * - 클릭으로 상세 모달 오픈 (스크롤 중 오동작 방지)
 *
 * @performance React.memo로 메모이제이션하여 불필요한 리렌더링 방지
 */

import { memo, useCallback } from 'react'
import type { Transaction } from '../types/Transaction'
import type { TransactionDisplayMode } from '../types/TransactionDisplayMode'
import type { Category } from '@/domains/category/types/Category'
import type { PaymentMethod } from '@/domains/payment/types/PaymentMethod'
import type { User } from '@/domains/user/types/User'

interface TransactionItemProps {
  transaction: Transaction
  paymentMethods: PaymentMethod[]
  users: User[]
  categories: Category[]
  onLongPress?: (id: string) => void
  displayMode?: TransactionDisplayMode
  isScrolling?: boolean  // 부모로부터 전달받는 스크롤 상태
}

export const TransactionItem = memo(function TransactionItem({
  transaction,
  paymentMethods,
  users,
  categories,
  onLongPress,
  displayMode = 'full',
  isScrolling = false
}: TransactionItemProps) {
  // 카테고리 정보 조회
  const category = categories.find(c =>
    c.id === (transaction.minorCategoryId || transaction.majorCategoryId)
  )

  // 대분류 조회
  const majorCategory = categories.find(c => c.id === transaction.majorCategoryId)

  // 카테고리 경로 구성 (대분류 > 소분류)
  const categoryPath = transaction.minorCategoryId
    ? `${majorCategory?.name} > ${category?.name}`
    : category?.name || '알 수 없음'

  // 결제수단 정보 조회
  const paymentMethod = paymentMethods.find(pm => pm.id === transaction.paymentMethodId)

  // 지출자 정보 조회
  const spender = users.find(u => u.id === transaction.spenderId)
  const spenderName = spender?.name || '알 수 없음'

  // "공용" 사용자일 때 아이콘 추가
  const spenderDisplay = transaction.spenderId === 'user_shared'
    ? `👥 ${spenderName}`
    : spenderName

  /**
   * 거래 아이템 클릭 핸들러
   *
   * @reason
   * - BUG-012 스크롤 시 롱프레스 오동작 방지
   * - 롱프레스 대신 단순 클릭으로 변경하여 즉시 반응성 향상
   * - isScrolling이 true이면 무시하여 스크롤 중 오동작 완전 방지
   *
   * @tradeoff
   * - 장점: 즉시 반응, 간단한 구현, 스크롤 중 보호
   * - 단점: 500ms 피드백 없음 (하지만 즉시 반응이므로 더 나음)
   */
  const handleClick = useCallback(() => {
    // 스크롤 중이면 무시
    if (isScrolling) {
      return
    }

    onLongPress?.(transaction.id)
  }, [isScrolling, transaction.id, onLongPress])

  return (
    <div
      className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer border-b border-gray-100 dark:border-gray-700 select-none"
      onClick={handleClick}
    >
      <div className="max-w-4xl mx-auto">
        {/* 1줄: 아이콘 + 메모 + 금액 */}
        <div className={`flex items-center justify-between ${displayMode === 'full' ? 'mb-1' : ''}`}>
          <div className="flex items-center gap-2 flex-1">
            <span className="text-xl">{category?.icon || '❓'}</span>
            <span className="text-gray-900 dark:text-gray-100 font-medium">
              {transaction.memo || '(메모 없음)'}
            </span>
          </div>
          <span
            className={`text-lg font-semibold ${
              transaction.type === 'income' ? 'text-income-fixed' : 'text-expense-fixed'
            }`}
          >
            {transaction.type === 'income' ? '+' : '-'}
            {transaction.amount.toLocaleString()}원
          </span>
        </div>

        {/* 2줄: 카테고리 · 결제수단 · 지출자 (반응형) */}
        {displayMode === 'full' && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 ml-7">
            {/* 작은 화면: 대분류만 표시 */}
            <span className="truncate max-w-[100px] sm:hidden">
              {majorCategory?.name || category?.name || '알 수 없음'}
            </span>

            {/* 큰 화면: 대분류 > 소분류 표시 */}
            <span className="hidden sm:inline truncate max-w-[100px] sm:max-w-[150px]">
              {categoryPath}
            </span>

            {/* 결제수단은 있을 때만 표시 (수입 등 결제수단 없는 거래는 생략) */}
            {paymentMethod && (
              <>
                <span>·</span>
                <span>{paymentMethod.name}</span>
              </>
            )}
            <span>·</span>
            <span>{spenderDisplay}</span>
          </div>
        )}
      </div>
    </div>
  )
})
